import React, {useEffect, useRef, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import {useSearchParams} from "react-router";
import {addMinutes, format, subDays} from "date-fns";
import {FaEraser, FaSearch} from "react-icons/fa";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import {Modal} from "../../../components/modal/modal.jsx";
import Button from "../../../components/button/button.jsx";
import InputDatetime from "../../../components/input/input-datetime.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import useHasPermission from "../../../hooks/use-has-permission.js";
import projectUtils from "../../../utils/project-utils.js";
import IndividualAlarmService from "../../../services/web/individual-alarm-service.js";
import CorporationService from "../../../services/web/corporation-service.js";
import VmsService from "../../../services/web/vms-service.js";
import AnalysisToolService from "../../../services/web/analysis-tool-service.js";
import IndividualAlarmCard from "./components/individual-alarm-card.jsx";
import IndividualAlarmDetails from "./components/individual-alarm-details.jsx";
import Pagination from "./components/pagination.jsx";
import AlarmImageModal from "./components/alarm-image-modal.jsx";
import "./individual-alarm.css";

const API_URL = import.meta.env.VITE_API_URL;

const getInitialFilters = () => ({
    startDate: subDays(new Date(), 7),
    endDate: addMinutes(new Date(), 1),
    document: "",
    providers: [],
});

const INITIAL_PARAMS = {
    size: 20,
    page: 0,
    sort: "createdAt,desc",
};

const IndividualAlarm = () => {
    const [searchParams] = useSearchParams();
    const lastAlarmId = searchParams.get("lastAlarmId");

    const hasPermission = useHasPermission();
    const hasSignPermission = hasPermission("sentry.sign.individual.alarm");

    const [lastCreatedAlarmId, setLastCreatedAlarmId] = useState(lastAlarmId);
    const [filters, setFilters] = useState(getInitialFilters);
    const [requestParams, setRequestParams] = useState(INITIAL_PARAMS);
    const [alarms, setAlarms] = useState(undefined);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [selectedAlarm, setSelectedAlarm] = useState(null);
    const [selectedAlarmId, setSelectedAlarmId] = useState(undefined);
    const [modal, setModal] = useState({show: false, src: undefined});
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const scrollRef = useRef(null);
    const secondScrollRef = useRef(null);

    const {data: providers} = useQuery({
        queryFn: () => IndividualAlarmService.providers(),
        queryKey: ["individual-alarm-providers"]
    });

    const {data: corporation} = useQuery({
        queryFn: () => CorporationService.entity(),
        queryKey: ["corporation"]
    });

    const {data: vmsConfigurations} = useQuery({
        queryFn: () => VmsService.getVmsConfigurationsByUser(),
        queryKey: ["vms-configurations-by-user"]
    });

    const {data: config} = useQuery({
        queryFn: () => AnalysisToolService.analysisConfig(),
        queryKey: ["analysis-config"]
    });

    const buildPayload = () => ({
        ...filters,
        startDate: filters.startDate ? format(filters.startDate, "yyyy-MM-dd'T'HH:mmxxx") : null,
        endDate: filters.endDate ? format(filters.endDate, "yyyy-MM-dd'T'HH:mmxxx") : null,
        providers: filters.providers?.map(item => ({id: item.value, name: item.label, type: item.type}))
    });

    const search = async () => {
        setIsLoading(true);
        setFilterModalOpen(false);

        await IndividualAlarmService.search(buildPayload(), requestParams)
            .then((data) => {
                setAlarms(data?.content);
                setTotalPages(data.totalPages);
                setTotalElements(data.totalElements);

                const found = (data?.content ?? []).filter(item => item.id === Number(lastCreatedAlarmId ?? 0));
                if (found.length > 0) {
                    setLastCreatedAlarmId(null);
                    setSelectedAlarm(found[0]);
                }
            })
            .catch(() => {
                projectUtils.swalErrorMessage('', "Erro ao realizar a pesquisa.", 'Ok');
                setIsLoading(false);
                setFilters(getInitialFilters());
            });

        setSelectedAlarmId(lastAlarmId);
        setIsLoading(false);
    };

    useEffect(() => {
        search();
    }, [requestParams]);

    useEffect(() => {
        if (!alarms) return;

        const found = alarms.filter(item => item.id === selectedAlarmId);
        if (found.length > 0) setSelectedAlarm(found[0]);
    }, [selectedAlarmId]);

    const scrollReset = (...refs) => refs.forEach(ref => ref.current?.scrollTo(0, 0));

    const openModal = (e, src) => {
        e.stopPropagation();
        setModal({show: true, src});
    };
    const closeModal = () => setModal({show: false, src: undefined});

    const handleInput = ({target: {id, value}}) => setFilters(prev => ({...prev, [id]: value}));
    const handleDate = (item, value) => setFilters(prev => ({...prev, [item]: value}));

    const clearFilters = () => setFilters(getInitialFilters());

    const onKeyDown = ({key}) => key === "Enter" && search();

    const onCardClick = (alarm) => {
        setSelectedAlarm(alarm);
        scrollReset(secondScrollRef);
    };

    const updateCurrentPage = (currentPage) => {
        setRequestParams(prev => ({...prev, page: currentPage}));
        scrollReset(scrollRef, secondScrollRef);
    };

    const returnToDefault = () => setSelectedAlarm(null);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Alarmes de indivíduos</Template.Title>
            </Template.Header>

            <Template.Body>
                <div className={"flex gap-2"}>
                    <div className={"w-1/4 flex flex-col gap-2 ia-full-height"}>
                        <Card>
                            <Card.Body>
                                <Button target={"primary"} className={"w-full font-bold text-[16px]"} onClick={() => setFilterModalOpen(true)}>
                                    Filtros
                                </Button>
                            </Card.Body>
                        </Card>

                        <Card className={"flex-1 flex flex-col min-h-0"}>
                            <Card.Body className={"flex-1 flex flex-col min-h-0"}>
                                <div ref={scrollRef} className={"flex-1 overflow-y-auto overflow-x-hidden min-h-0"}>
                                    {
                                        alarms && alarms.map((alarm, index) => (
                                            <IndividualAlarmCard
                                                key={index}
                                                alarm={alarm}
                                                openModal={openModal}
                                                onCardClick={onCardClick}
                                            />
                                        ))
                                    }
                                    {
                                        alarms && !alarms.length &&
                                        <div id={"empty-list"} className={"rounded border border-sky-200 bg-sky-100 text-sky-800 font-bold p-3 m-0"}>
                                            Nenhum registro encontrado.
                                        </div>
                                    }
                                </div>

                                <div className={"flex w-full"}>
                                    <Pagination
                                        updateCurrentPage={updateCurrentPage}
                                        requestParams={requestParams}
                                        totalPages={totalPages}
                                        totalElements={totalElements}
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </div>

                    <Card className={"w-3/4 ia-full-height"}>
                        <Card.Body className={"h-full"}>
                            {
                                !selectedAlarm &&
                                <div className={"ia-entity-div"}>
                                    <img className={"ia-entity-logo"} src={`${API_URL}/corporation/entity/image`} alt="entity-logo"/>
                                </div>
                            }
                            {
                                selectedAlarm &&
                                <IndividualAlarmDetails
                                    latitude={corporation?.latitude}
                                    longitude={corporation?.longitude}
                                    mapsKey={corporation?.maps_api_key}
                                    vmsConfigurations={vmsConfigurations}
                                    hasSignPermission={hasSignPermission}
                                    hasDispatch={config?.has_dispatch}
                                    selected={selectedAlarm}
                                    secondScrollRef={secondScrollRef}
                                    openModal={openModal}
                                    setIsLoading={setIsLoading}
                                    search={search}
                                    setSelectedAlarmId={setSelectedAlarmId}
                                    returnToDefault={returnToDefault}
                                />
                            }
                        </Card.Body>
                    </Card>
                </div>
            </Template.Body>

            <AlarmImageModal open={modal.show} onClose={closeModal} src={modal.src}/>

            <Modal open={filterModalOpen} setOpen={setFilterModalOpen} title={"Filtros"} className={"w-96"} overflow>
                <Modal.Body className={"flex flex-col gap-4"}>
                    <InputDatetime
                        id={"startDate"}
                        label={"Data/hora inicial *"}
                        dateFormat={"LL"}
                        selected={filters.startDate}
                        onChange={(date) => handleDate("startDate", date)}
                    />

                    <InputDatetime
                        id={"endDate"}
                        label={"Data/hora final *"}
                        dateFormat={"LL"}
                        selected={filters.endDate}
                        onChange={(date) => handleDate("endDate", date)}
                    />

                    <InputForm
                        id={"document"}
                        label={"CPF"}
                        value={filters.document}
                        onChange={handleInput}
                        onKeyDown={onKeyDown}
                        mask={'000.000.000-00'}
                    />

                    <SelectComponent
                        value={filters.providers}
                        options={providers ?? []}
                        label={"Câmeras / Entidades"}
                        onChange={(value) => setFilters(prev => ({...prev, providers: value ?? []}))}
                        isMulti
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button target={"primary"} className={"w-full"} onClick={search}>
                        <FaSearch/> Pesquisar
                    </Button>
                    <Button target={"primary-outline"} className={"w-full"} onClick={clearFilters}>
                        <FaEraser/> Limpar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Template>
    );
};

export default IndividualAlarm;
